import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import { carrierEndpoint } from "../utils/trustCallApi";
import type { TrustCallPhone } from "../types";

// Hardcoded test destinations matching MVC Monitor.cshtml lines 2056-2060.
// Values are 10-digit phone numbers (no country code prefix — backend adds "1").
const DESTINATIONS = [
  { value: "7077999400", label: "Ronnie Phone 1 (+1 707-799-9400)" },
  { value: "9714648899", label: "Ronnie Phone 2 (+1 971-464-8899)" },
  { value: "5039367187", label: "Jeff (+1 503-936-7187)" },
];

// Duration options matching MVC Monitor.cshtml lines 2107-2111.
// Preset values use context strings (sent as-is to AdhocDialer API).
// "custom" triggers a separate durationSeconds field.
const DURATION_OPTIONS = [
  { value: "30s", label: "30 seconds" },
  { value: "2m", label: "2 minutes" },
  { value: "4m", label: "4 minutes" },
  { value: "5m", label: "5 minutes" },
  { value: "custom", label: "Custom..." },
];

interface OriginateCallModalProps {
  show: boolean;
  phone: TrustCallPhone | null;
  onClose: () => void;
}

/**
 * Originate Call modal — exact match of MVC Kendo Dialog (Monitor.cshtml lines 2046-2085).
 *
 * Layout:
 *   - Caller ID (Selected Number) — read-only input
 *   - Destination — dropdown with hardcoded test numbers
 *   - Duration — dropdown (30s / 2m / 4m / 5m / Custom...)
 *   - Custom Duration (seconds) — shown only when "Custom..." selected, default 60
 *   - Buttons: Cancel + Originate Call (primary)
 */
const OriginateCallModal: React.FC<OriginateCallModalProps> = ({
  show,
  phone,
  onClose,
}) => {
  const [destination, setDestination] = useState(DESTINATIONS[0].value);
  const [durationSelection, setDurationSelection] = useState("30s");
  const [customDuration, setCustomDuration] = useState(60);

  const isCustom = durationSelection === "custom";

  const originateMutation = useMutation({
    mutationFn: (data: { destination: string; context?: string; durationSeconds?: number }) =>
      callApi(
        carrierEndpoint(phone?.phoneNumberClean || phone?.phoneNumber.replace(/\D/g, "") || "", "originate-call"),
        HttpMethod.POST,
        data
      ),
    onSuccess: () => {
      const durationLabel = isCustom
        ? `${customDuration} seconds`
        : DURATION_OPTIONS.find((d) => d.value === durationSelection)?.label ?? "";
      const destLabel = DESTINATIONS.find((d) => d.value === destination)?.label ?? destination;
      toast.success(`Call initiated successfully!\n\nCaller ID: ${phone?.phoneNumber}\nDestination: ${destLabel}\nDuration: ${durationLabel}`);
      handleClose();
    },
    onError: (error: Error) =>
      toast.error(error.message || "Failed to initiate call"),
  });

  const handleClose = () => {
    setDestination(DESTINATIONS[0].value);
    setDurationSelection("30s");
    setCustomDuration(60);
    onClose();
  };

  const handleSubmit = () => {
    if (isCustom && (customDuration < 1 || customDuration > 3600)) {
      toast.error("Custom duration must be between 1 and 3600 seconds");
      return;
    }
    // Match MVC: presets send context, custom sends durationSeconds
    originateMutation.mutate(
      isCustom
        ? { destination, durationSeconds: customDuration }
        : { destination, context: durationSelection }
    );
  };

  if (!show || !phone) return null;

  return (
    <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog" style={{ maxWidth: 490 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Originate Call</h5>
            <button type="button" className="btn-close" onClick={handleClose} disabled={originateMutation.isPending} />
          </div>
          <div className="modal-body">
            {/* Caller ID (Selected Number) — read-only */}
            <div className="mb-3">
              <label className="form-label">Caller ID (Selected Number)</label>
              <input
                type="text"
                className="form-control"
                readOnly
                value={phone.phoneNumber}
              />
            </div>

            {/* Destination — hardcoded test numbers */}
            <div className="mb-3">
              <label className="form-label">Destination</label>
              <select
                className="form-select"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                disabled={originateMutation.isPending}
              >
                {DESTINATIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Duration */}
            <div className="mb-3">
              <label className="form-label">Duration</label>
              <select
                className="form-select"
                value={durationSelection}
                onChange={(e) => setDurationSelection(e.target.value)}
                disabled={originateMutation.isPending}
              >
                {DURATION_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Duration (seconds) — only visible when "Custom..." selected */}
            {isCustom && (
              <div className="mb-3">
                <label className="form-label">Custom Duration (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  max={3600}
                  value={customDuration}
                  onChange={(e) => setCustomDuration(Number(e.target.value))}
                  disabled={originateMutation.isPending}
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={originateMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={originateMutation.isPending}
            >
              {originateMutation.isPending ? (
                <><span className="spinner-border spinner-border-sm me-1" />Calling...</>
              ) : (
                "Originate Call"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OriginateCallModal;
